<template>
    <Card class="w-full">
        <template #header>
            <div class="flex items-center gap-2 p-4 pb-0">
                <i class="pi pi-plus text-xl"></i>
                <h3 class="text-lg font-semibold">{{ $t('createOneTimeTask') }}</h3>
            </div>
        </template>
        <template #content>
            <Form v-slot="$form" :initialValues="initialValues" :resolver="resolver" @submit="handleSubmit"
                ref="formRef">
                <div class="space-y-4">
                    <!-- Name Field -->
                    <div>
                        <FloatLabel variant="in" fluid>
                            <InputText id="name" name="name" fluid type="text" />
                            <label for="name">{{ $t("name") }}*</label>
                        </FloatLabel>
                        <Message v-if="$form.name?.invalid" severity="error" size="small" variant="simple">
                            {{ $form.name.error?.message }}
                        </Message>
                    </div>

                    <!-- Description Field -->
                    <div>
                        <FloatLabel variant="in" fluid>
                            <Textarea id="description" name="description" rows="3" cols="30" style="resize: none"
                                fluid />
                            <label for="description">{{ $t("description") }}</label>
                        </FloatLabel>
                        <Message v-if="$form.description?.invalid" severity="error" size="small" variant="simple">
                            {{ $form.description.error?.message }}
                        </Message>
                    </div>

                    <!-- Category and Difficulty Row -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <FloatLabel variant="in" fluid>
                                <Select id="categoryId" name="categoryId" :options="sortedCategories" filter
                                    optionLabel="name" option-value="id" class="w-full">
                                    <template #footer>
                                        <div class="p-3">
                                            <Button :label="$t('settings.categories.addCategory')" fluid
                                                severity="secondary" variant="text" size="small" icon="pi pi-plus"
                                                @click="openCreateCategoryDialog" />
                                        </div>
                                    </template>
                                </Select>
                                <label for="categoryId">{{ $t("category") }}*</label>
                            </FloatLabel>
                            <Message v-if="$form.categoryId?.invalid" severity="error" size="small" variant="simple">
                                {{ $form.categoryId.error?.message }}
                            </Message>
                        </div>
                        <div>
                            <FloatLabel variant="in" fluid>
                                <Select id="difficultyLevel" name="difficultyLevel" :options="sortedDifficulties"
                                    option-label="label" option-value="value" filter class="w-full" />
                                <label for="difficultyLevel">{{ $t("difficulty") }}*</label>
                            </FloatLabel>
                            <Message v-if="$form.difficultyLevel?.invalid" severity="error" size="small"
                                variant="simple">
                                {{ $form.difficultyLevel.error?.message }}
                            </Message>
                        </div>
                    </div>

                    <!-- Estimated Minutes Field -->
                    <div>
                        <FloatLabel variant="in" fluid>
                            <InputNumber id="estimatedMinutes" name="estimatedMinutes" fluid :min="1" :max="480"
                                :allow-empty="false" suffix=" min" />
                            <label for="estimatedMinutes">{{ $t("estimatedMinutes") }}*</label>
                        </FloatLabel>
                        <Message v-if="$form.estimatedMinutes?.invalid" severity="error" size="small" variant="simple">
                            {{ $form.estimatedMinutes.error?.message }}
                        </Message>
                    </div>

                    <!-- Participants and Date Row -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <FloatLabel variant="in" fluid>
                                <DatePicker v-model="selectedDate" inputId="selectedDate" fluid showIcon
                                    dateFormat="dd.mm.yy" :manualInput="false" showButtonBar />
                                <label for="selectedDate">{{ $t("performedAt") }}</label>
                            </FloatLabel>
                        </div>
                        <div>
                            <FloatLabel variant="in" fluid>
                                <MultiSelect v-model="selectedUserIds" :options="filteredAndSortedUsers" filter
                                    optionLabel="username" option-value="id" fluid showClear id="selectedUserIds" />
                                <label for="selectedUserIds">{{ $t("performedTogetherWith") }}</label>
                            </FloatLabel>
                        </div>
                    </div>

                    <!-- Submit Button -->
                    <div class="flex gap-2 pt-2">
                        <Button type="button" :label="$t('cancel')" severity="secondary" outlined
                            @click="$emit('cancel')" class="flex-1" />
                        <Button type="submit" :label="$t('submit')" icon="pi pi-save" iconPos="right"
                            :disabled="submitDisabled" :loading="isSubmitting" class="flex-1" />
                    </div>
                </div>
            </Form>
        </template>
    </Card>
    <AddCategoryDialog v-model:visible="showCreateCategoryDialog" @submitted="handleCategoryCreated"
        @cancel="handleCategoryDialogCancel" />
</template>

<script setup lang="ts">
import AddCategoryDialog from "@/components/settings/AddCategoryDialog.vue";
import { ref, computed, onMounted, useTemplateRef } from 'vue';
import { storeToRefs } from 'pinia';
import { useChoreSettingsStore, useUsersStore, useTaskBoardStore } from '@/stores';
import { useToast } from 'primevue/usetoast';
import { useI18n } from 'vue-i18n';
import { Form, type FormSubmitEvent } from '@primevue/forms';
import { zodResolver } from '@primevue/forms/resolvers/zod';
import { z } from 'zod';
import { DifficultyLevel, type CreateAndPerformOneTimeChoreDto, type UserDto } from '@/client';

const showCreateCategoryDialog = ref(false);

const { t } = useI18n();
const toast = useToast();
const formRef = useTemplateRef('formRef');

const props = defineProps<{
    users: UserDto[];
}>();

const emit = defineEmits<{
    (e: 'submitted'): void;
    (e: 'cancel'): void;
}>();

// Stores
const choreSettingsStore = useChoreSettingsStore();
const usersStore = useUsersStore();
const taskBoardStore = useTaskBoardStore();

const { performChores } = taskBoardStore;
const { ensureChoreSettingsDataLoaded } = choreSettingsStore;
const { categories } = storeToRefs(choreSettingsStore);
const { user } = storeToRefs(usersStore);

const { performedChoreSummary } = storeToRefs(taskBoardStore);

// Form state
const isSubmitting = ref(false);
const selectedDate = ref<Date>(new Date());
const selectedUserIds = ref<string[]>([]);

// Form validation
const initialValues = ref({
    name: '',
    description: '',
    categoryId: '',
    difficultyLevel: '',
    estimatedMinutes: 15,
});

const resolver = computed(() => {
    return zodResolver(
        z.object({
            name: z.string()
                .min(1, { message: t('validation.nameRequired') })
                .min(3, { message: t('validation.nameMinLength') })
                .max(100, { message: t('validation.nameMaxLength') }),
            description: z.string()
                .max(500, { message: t('validation.descriptionMaxLength') })
                .optional(),
            categoryId: z.string()
                .uuid({ message: t('validation.categoryRequired') }),
            difficultyLevel: z.enum(Object.values(DifficultyLevel), { message: t('validation.difficultyRequired') }),
            estimatedMinutes: z.number()
                .min(1, { message: t('validation.estimatedMinutesMin') })
                .max(480, { message: t('validation.estimatedMinutesMax') })
        })
    );
});

// Computed properties
const sortedCategories = computed(() =>
    [...(categories.value || [])].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
);

const sortedDifficulties = [
    { value: DifficultyLevel.EASY, label: t('difficultyLevels.' + DifficultyLevel.EASY) },
    { value: DifficultyLevel.MEDIUM, label: t('difficultyLevels.' + DifficultyLevel.MEDIUM) },
    { value: DifficultyLevel.HARD, label: t('difficultyLevels.' + DifficultyLevel.HARD) },
    { value: DifficultyLevel.VERY_HARD, label: t('difficultyLevels.' + DifficultyLevel.VERY_HARD) },
];

onMounted(async () => {
    await ensureChoreSettingsDataLoaded();
});

const filteredAndSortedUsers = computed(() => {
    return props.users
        .filter(u => u.id! !== user.value?.id!)
        .sort((a, b) => a.username!.localeCompare(b.username!));
});

const submitDisabled = computed(() => {
    return !user.value || selectedDate.value == null || isSubmitting.value;
});

// Methods
async function handleSubmit(e: FormSubmitEvent<Record<string, any>>) {
    if (!e.valid) {
        console.error("Form validation failed:", e.errors);
        return;
    }

    if (submitDisabled.value) {
        return;
    }

    isSubmitting.value = true;
    try {
        const oneTimeChore: CreateAndPerformOneTimeChoreDto = {
            name: e.values.name,
            description: e.values.description || null,
            categoryId: e.values.categoryId,
            difficultyLevel: e.values.difficultyLevel,
            estimatedMinutes: e.values.estimatedMinutes,
        };

        const performedChoresInfo = await performChores(
            [], // No regular chores
            [oneTimeChore], // One-time chore
            selectedUserIds.value,
            selectedDate.value!
        );

        console.log("Performed chores info:", performedChoresInfo);
        toast.add({
            severity: 'success',
            summary: t('taskSubmitted'),
            detail: t('taskCompleted', 1),
            life: 5000
        });
        performedChoreSummary.value = performedChoresInfo;

        // Reset form
        resetForm();
        emit('submitted');
    } catch (error) {
        console.error("Error while submitting one-time chore:", error);
        toast.add({
            severity: 'error',
            summary: t('error'),
            detail: t('oneTimeTaskSubmissionError'),
            life: 5000
        });
    } finally {
        isSubmitting.value = false;
    }
}

function resetForm() {
    Object.assign(initialValues.value, {
        name: '',
        description: '',
        categoryId: '',
        difficultyLevel: '',
        estimatedMinutes: 15,
    });
    selectedDate.value = new Date();
    selectedUserIds.value = [];
    (formRef.value as any)?.reset();
}

function openCreateCategoryDialog() {
    showCreateCategoryDialog.value = true;
}

function handleCategoryCreated() {
    showCreateCategoryDialog.value = false;
    toast.add({
        severity: 'success',
        summary: t('settings.categories.categoryCreated'),
        detail: t('settings.categories.categoryCreatedDetail'),
        life: 3000
    });
}

function handleCategoryDialogCancel() {
    showCreateCategoryDialog.value = false;
}
</script>
